import { Nav, Navbar, Offcanvas } from "react-bootstrap";
import { startTransition, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { BsThreeDots, BsBell, BsGear, BsPersonPlus } from "react-icons/bs";
import CreateOrderIcon from "../../../images/icons/navMenu/createOrder.svg?react";
import ProfileIcon from "../../../images/icons/navMenu/profile.svg?react";
import ShipmentsIcon from "../../../images/icons/navMenu/shipments.svg?react";
import AddressBookIcon from "../../../images/icons/navMenu/addressBook.svg?react";
import SupportIcon from "../../../images/icons/navMenu/support.svg?react";
import MySuggestionsIcon from "../../../images/icons/navMenu/mySuggestions.svg?react";
import { useClearingOrder } from "../../../hooks/useClearingOrder";
import { routes } from "../../../app/routes/routes";
import { useUserRole } from "../../../hooks/useUserRole";
import { UserRolesEnum } from "../../../app/enums/userRolesEnum";
import "./BottomNavbar.scss";

const BottomNavbar = () => {
    const [showMore, setShowMore] = useState(false);
    const [clearOrder] = useClearingOrder();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { session, renderIfHasRole } = useUserRole();
    const location = useLocation();

    const handleCreateOrderClick = () => {
        startTransition(() => {
            clearOrder();
            navigate(routes.ordering.path);
        });
    };

    const handleNavigate = (path: string) => {
        setShowMore(false);
        navigate(path);
    };

    const isActiveRoute = useCallback(
        (path: string) => location.pathname === path,
        [location.pathname]
    );

    if (!session) return null;

    return (
        <>
            <Navbar className="bottom-nav" data-bs-theme="dark" fixed="bottom">
                <Nav>
                    <Nav.Item>
                        <Nav.Link
                            onClick={() => handleNavigate(routes.profile.path)}
                            className={isActiveRoute(routes.profile.path) ? "active" : ""}
                        >
                            <ProfileIcon title={`${t("navbar.sidebar.linkTitels.profile")} icon`} />
                        </Nav.Link>
                    </Nav.Item>

                    <Nav.Item>
                        <Nav.Link
                            onClick={() => handleNavigate(routes.myOrders.path)}
                            className={isActiveRoute(routes.myOrders.path) ? "active" : ""}
                        >
                            <ShipmentsIcon title={`${t("navbar.sidebar.linkTitels.myOrders")} icon`} />
                        </Nav.Link>
                    </Nav.Item>

                    <Nav.Item className="create-order">
                        <Nav.Link onClick={handleCreateOrderClick}>
                            <CreateOrderIcon title={`${t("navbar.sidebar.linkTitels.createOrder")} icon`} />
                        </Nav.Link>
                    </Nav.Item>

                    <Nav.Item>
                        <Nav.Link
                            onClick={() => handleNavigate(routes.addressBook.path)}
                            className={isActiveRoute(routes.addressBook.path) ? "active" : ""}
                        >
                            <AddressBookIcon title={`${t("navbar.sidebar.linkTitels.addressBook")} icon`} />
                        </Nav.Link>
                    </Nav.Item>

                    <Nav.Item>
                        <Nav.Link
                            onClick={() => setShowMore(true)}
                            className={showMore ? "active" : ""}
                        >
                            <BsThreeDots style={{ minHeight: "34px", minWidth: "24px" }} />
                        </Nav.Link>
                    </Nav.Item>
                </Nav>
            </Navbar>

            <Offcanvas
                show={showMore}
                onHide={() => setShowMore(false)}
                placement="bottom"
                className="bottom-nav-offcanvas"
                data-bs-theme="dark"
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>{t("navbar.sidebar.linkTitels.more")}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <Nav className="bottom-nav-offcanvas__list">
                        <Nav.Item>
                            <Nav.Link
                                onClick={() => handleNavigate(routes.support.path)}
                                className={isActiveRoute(routes.support.path) ? "active" : ""}
                            >
                                <SupportIcon title={`${t("navbar.sidebar.linkTitels.support")} icon`} />
                                <span>{t("navbar.sidebar.linkTitels.support")}</span>
                            </Nav.Link>
                        </Nav.Item>

                        <Nav.Item>
                            <Nav.Link
                                onClick={() => handleNavigate(routes.mySuggestions.path)}
                                className={isActiveRoute(routes.mySuggestions.path) ? "active" : ""}
                            >
                                <MySuggestionsIcon title={`${t("navbar.sidebar.linkTitels.mySuggestions")} icon`} />
                                <span>{t("navbar.sidebar.linkTitels.mySuggestions")}</span>
                            </Nav.Link>
                        </Nav.Item>

                        <Nav.Item>
                            <Nav.Link
                                onClick={() => handleNavigate(routes.notifications.path)}
                                className={isActiveRoute(routes.notifications.path) ? "active" : ""}
                            >
                                <BsBell style={{ minHeight: "24px", minWidth: "24px" }} />
                                <span>{t("navbar.sidebar.linkTitels.notifications")}</span>
                            </Nav.Link>
                        </Nav.Item>

                        {renderIfHasRole(
                            <Nav.Item key="adminPanel">
                                <Nav.Link
                                    onClick={() => handleNavigate(routes.adminPanel.path)}
                                    className={isActiveRoute(routes.adminPanel.path) ? "active" : ""}
                                >
                                    <BsGear style={{ minHeight: "24px", minWidth: "24px" }} />
                                    <span>{t("navbar.sidebar.linkTitels.adminPanel")}</span>
                                </Nav.Link>
                            </Nav.Item>,
                            [UserRolesEnum.Admin]
                        )}

                        {renderIfHasRole(
                            <Nav.Item key="registerUser">
                                <Nav.Link
                                    onClick={() => handleNavigate(routes.signUp.path)}
                                    className={isActiveRoute(routes.signUp.path) ? "active" : ""}
                                >
                                    <BsPersonPlus style={{ minHeight: "24px", minWidth: "24px" }} />
                                    <span>{t("navbar.sidebar.linkTitels.registerUser")}</span>
                                </Nav.Link>
                            </Nav.Item>,
                            [UserRolesEnum.Admin]
                        )}
                    </Nav>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default BottomNavbar;
